import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';

const T = {
  title:         'Vikundi Kiganjani',
  tagline:       'Tengeneza Akaunti Yako',
  step1Title:    'Thibitisha Namba ya Simu',
  step2Title:    'Weka Msimbo wa SMS',
  step3Title:    'Weka Nywila',
  groupTitle:    'Chagua Kikundi',
  labelPhone:    'Namba ya Simu',
  labelOtp:      'Msimbo wa OTP (SMS)',
  labelPassword: 'Nywila Mpya',
  labelConfirm:  'Thibitisha Nywila',
  btnSendOtp:    'Tuma OTP',
  btnResend:     'Tuma tena',
  btnSending:    'Inatuma...',
  btnVerify:     'Thibitisha →',
  btnVerifying:  'Inahakiki...',
  btnCreate:     'Hifadhi Nywila →',
  btnCreating:   'Inahifadhi...',
  otpSent:       'Weka msimbo uliokuja kwenye SMS ya mwaliko',
  pwdHint:       'Angalau herufi 6',
  back:          '← Rudi nyuma',
  haveAccount:   'Una akaunti tayari?',
  loginLink:     'Ingia hapa',
  footer:        '© Vikundi Kiganjani',
};

const bgStyle = { background: 'linear-gradient(135deg,#064e3b 0%,#065f46 50%,#047857 100%)' };
const btnStyle = (disabled) => ({
  background: disabled ? '#9ca3af' : 'linear-gradient(135deg,#059669,#0d9488)',
});

export default function Setup() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [searchParams] = useSearchParams();

  const [step, setStep]             = useState('phone'); // phone | otp | group | password
  const [phone, setPhone]           = useState('');
  const [otpCode, setOtpCode]       = useState('');
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPwd, setShowPwd]       = useState(false);
  const [showCfm, setShowCfm]       = useState(false);
  const [setupToken, setSetupToken] = useState('');
  const [groupData, setGroupData]   = useState(null);
  const [countdown, setCountdown]   = useState(0);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [otpSent, setOtpSent]       = useState(false);

  // Invitation link: ?mid=X&otp=CODE → auto-verify and jump to password step
  useEffect(() => {
    const mid = searchParams.get('mid');
    const otp = searchParams.get('otp');
    if (!mid) return;

    api.get(`/auth/member-hint/${mid}`).then(async ({ data }) => {
      const memberPhone = data.phone;
      setPhone(memberPhone);

      if (otp) {
        // Auto-verify the OTP embedded in the invitation link
        setLoading(true);
        try {
          const { data: vData } = await api.post('/auth/verify-otp', { phone: memberPhone, otp_code: otp });
          if (vData.needs_group_select) {
            setGroupData(vData);
            setStep('group');
          } else if (vData.needs_setup) {
            setSetupToken(vData.setup_token);
            setStep('password');
          }
        } catch {
          // OTP expired or invalid — fall back to OTP entry step
          setOtpSent(true);
          setStep('otp');
        } finally {
          setLoading(false);
        }
      } else {
        setOtpSent(true);
        setStep('otp');
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleSendOtp = async () => {
    if (!phone) { setError('Weka namba ya simu kwanza'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/request-otp', { phone });
      setOtpSent(true);
      setOtpCode('');
      setCountdown(60);
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.error || 'Imeshindwa kutuma OTP. Jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', { phone, otp_code: otpCode });

      if (data.needs_group_select) {
        setGroupData(data);
        setStep('group');
      } else if (data.needs_setup) {
        setSetupToken(data.setup_token);
        setStep('password');
      } else if (data.token) {
        // Already has account (e.g. password reset)
        login(data.token, data.user);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'OTP si sahihi. Jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  const handleChooseGroup = async (memberId, effectiveRole) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/choose-group', {
        select_token:   groupData.select_token,
        member_id:      memberId,
        effective_role: effectiveRole,
      });
      if (data.needs_setup) {
        setSetupToken(data.setup_token);
        setGroupData(null);
        setStep('password');
      } else if (data.token) {
        login(data.token, data.user);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Imeshindwa kuchagua kikundi. Jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Nywila hazifanani'); return; }
    if (password.length < 6) { setError('Nywila lazima iwe na herufi 6 au zaidi'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/setup-password', {
        setup_token: setupToken,
        password,
        confirm_password: confirm,
      });
      login(data.token, data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.error || 'Imeshindwa kuunda akaunti. Jaribu tena.');
    } finally {
      setLoading(false);
    }
  };

  const stepLabel = { phone: '1', otp: '2', group: '2', password: searchParams.get('otp') ? '2' : '3' };
  const stepTitle = {
    phone:    T.step1Title,
    otp:      T.step2Title,
    group:    T.groupTitle,
    password: T.step3Title,
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-white/5 blur-3xl" />
        <div className="absolute top-1/2 -right-40 h-96 w-96 rounded-full bg-teal-400/10 blur-3xl" />
        <div className="absolute -bottom-40 left-1/3 h-[400px] w-[400px] rounded-full bg-emerald-300/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Brand */}
        <div className="text-center mb-8">
          <div
            className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl shadow-xl"
            style={{ background: 'linear-gradient(135deg,#10b981,#0d9488)' }}
          >
            <span className="text-4xl">🌿</span>
          </div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight">{T.title}</h1>
          <p className="text-emerald-200 text-sm mt-1.5">{T.tagline}</p>
        </div>

        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          {/* Step header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-5 flex items-center gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/25 text-white font-bold text-sm">
              {stepLabel[step]}
            </div>
            <div>
              <p className="text-white font-bold">{stepTitle[step]}</p>
              <p className="text-emerald-100 text-xs">
                {step === 'phone' && 'Tutakutumia OTP kwa SMS'}
                {step === 'otp'   && 'Angalia ujumbe wa SMS uliotumwa'}
                {step === 'group' && 'Uko kwenye vikundi vingi'}
                {step === 'password' && 'Nywila itumike kuingia mara zote'}
              </p>
            </div>
          </div>

          <div className="px-8 py-7">
            {error && (
              <div className="mb-5 flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <span className="mt-0.5">⚠️</span><span>{error}</span>
              </div>
            )}

            {/* ── Step: phone ── */}
            {step === 'phone' && (
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {T.labelPhone}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">📞</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="0700000000"
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
                    />
                  </div>
                </div>
                <button
                  onClick={handleSendOtp}
                  disabled={loading || !phone}
                  className="w-full rounded-xl py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
                  style={btnStyle(loading || !phone)}
                >
                  {loading ? T.btnSending : T.btnSendOtp}
                </button>
              </div>
            )}

            {/* ── Step: OTP ── */}
            {step === 'otp' && (
              <form onSubmit={handleVerifyOtp} className="space-y-5">
                {otpSent && (
                  <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
                    <span>✅</span><span>{T.otpSent}</span>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {T.labelOtp}
                    </label>
                    {countdown > 0 ? (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{countdown}s</span>
                    ) : (
                      <button type="button" onClick={handleSendOtp} disabled={loading}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50">
                        {loading ? T.btnSending : T.btnResend}
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={otpCode}
                    onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="– – – – – –"
                    required maxLength={6}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-center tracking-[0.5em] text-2xl font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || otpCode.length < 6}
                  className="w-full rounded-xl py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-200 disabled:opacity-60 active:scale-95"
                  style={btnStyle(loading || otpCode.length < 6)}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      {T.btnVerifying}
                    </span>
                  ) : T.btnVerify}
                </button>
                <button type="button" onClick={() => { setStep('phone'); setError(''); }}
                  className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors">
                  {T.back}
                </button>
              </form>
            )}

            {/* ── Step: group select ── */}
            {step === 'group' && groupData && (
              <div className="space-y-3">
                {groupData.groups.map(g => (
                  <button
                    key={`${g.member_id}_${g.effective_role}`}
                    onClick={() => handleChooseGroup(g.member_id, g.effective_role)}
                    disabled={loading}
                    className="group w-full rounded-2xl border-2 border-gray-100 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50 p-4 text-left transition-all duration-200 disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                        <span className="text-lg">👥</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{g.name}</p>
                        <p className="text-xs text-emerald-600 mt-0.5 capitalize font-medium">{g.role}</p>
                      </div>
                      <span className="text-gray-300 group-hover:text-emerald-500 text-xl font-light">›</span>
                    </div>
                  </button>
                ))}
                <button type="button" onClick={() => { setStep('otp'); setError(''); }}
                  className="w-full text-sm text-gray-400 hover:text-gray-600 py-1 transition-colors">
                  {T.back}
                </button>
              </div>
            )}

            {/* ── Step: set password ── */}
            {step === 'password' && (
              <form onSubmit={handleSetPassword} className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {T.labelPassword}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔑</span>
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required minLength={6}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-12 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
                    />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPwd ? '🙈' : '👁️'}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">{T.pwdHint}</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {T.labelConfirm}
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                    <input
                      type={showCfm ? 'text' : 'password'}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      required minLength={6}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 pl-11 pr-12 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
                    />
                    <button type="button" onClick={() => setShowCfm(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showCfm ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {confirm && password !== confirm && (
                    <p className="mt-1.5 text-xs text-red-500">Nywila hazifanani</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || password.length < 6 || password !== confirm}
                  className="w-full rounded-xl py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-200 disabled:opacity-60 active:scale-95"
                  style={btnStyle(loading || password.length < 6 || password !== confirm)}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      {T.btnCreating}
                    </span>
                  ) : T.btnCreate}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Link to login */}
        <p className="mt-5 text-center text-sm text-emerald-200">
          {T.haveAccount}{' '}
          <button onClick={() => navigate('/login')}
            className="font-bold text-white underline underline-offset-2 hover:text-emerald-100">
            {T.loginLink}
          </button>
        </p>

        <p className="mt-3 text-center text-xs text-emerald-300/70">
          {T.footer} · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
